/* eslint-disable @reatom/unit-naming-rule */
import { action, atom, onConnect, reatomArray } from '@reatom/framework'

import { PoolEvent } from '@blade/blockchain-types'
import { Array, AsyncStateN, pipe, Result } from '@blade/std-lib'
import { selectedNetworkAtom } from '@root/entities/network'
import { reatomPair } from '@root/entities/pair'
import { pairRegistry } from '@root/entities/pair/atoms'
import { subscribeOnPairEventsAction } from '@root/features/realtime/atoms'
import { processPairEventsUpdateAction } from '@root/features/realtime/utils'
import { reatomRequest } from '@root/shared/lib'

import { fetchTrendingPairsRequestAction } from './trending-requests'
import {
  DEFAULT_TRENDING_STRATEGY,
  trendingStrategyAtom,
} from './trending-strategy'

import type { Network } from '@root/entities/network'
import type { PairEventPayload } from '@root/features/realtime/atoms'

/** How long the list waits for the socket before fetching one itself. */
const SNAPSHOT_GRACE_MS = 1500

/**
 * How often a list the socket does not push gets read again.
 *
 * Only the strategies that are not ours need it, and they are ranked over a
 * day, so a list that is half a minute old is the same list.
 */
/**
 * How many pools the strips built on this model ask for.
 *
 * It asked for none, and a feed with no limit answers with its maximum: a
 * hundred pool cards, **250 KB compressed on production 2026-10-02**, every
 * thirty seconds, on every screen, because the topbar's strip is on all of
 * them. A scrolling line of tickers and a drawer of recent pairs are both
 * read by eye; thirty is a long loop and a quarter of the bytes.
 *
 * The strip wants five fields of each and there is a route that returns just
 * those — `pools/ticker`, twenty times smaller again. It cannot be used here
 * yet: these are pair models, and the row draws a premium badge, a migration
 * marker and a provider mark off them, so moving would drop all three.
 */
const STRIP_ROWS = 30

const POLL_MS = 30_000

const reatomTrendingPairs = (network: Network.Network, strategy: string) => {
  const live = strategy === DEFAULT_TRENDING_STRATEGY

  const fetchPairs = action(async (ctx) => {
    const result = await ctx.schedule(() =>
      fetchTrendingPairsRequestAction(ctx, {
        network,
        limit: STRIP_ROWS,
        ...(live ? {} : { strategy }),
      }),
    )

    return result.pipe(
      Result.map((it) => {
        const pairs = it.data.map((dto) =>
          pairRegistry.resolveFromDto(ctx, dto),
        )
        return reatomArray(pairs)
      }),
    )
  }, 'fetchPairs')

  const pairsAtom = reatomRequest({
    execAction: fetchPairs,
    name: `reatomTrendingPairs_${network}`,
  })

  const initialLoadAction = action((ctx) => {
    const state = ctx.get(pairsAtom)
    if (AsyncStateN.isSuccess(state)) {
      pairsAtom.refresh(ctx, { strategy: 'success-only' })
      return
    }
    pairsAtom.execute(ctx)
  }, 'initialLoadAction')

  const handlePairEvents = action((ctx, data: PairEventPayload) => {
    if (data.updateType === 'replace') {
      const pairs = data.pairs.map((dto) =>
        pairRegistry.resolveFromDto(ctx, dto),
      )
      pairsAtom.setState(ctx, AsyncStateN.success(reatomArray(pairs)))
      return
    }

    const state = ctx.get(pairsAtom)
    if (!AsyncStateN.isSuccess(state)) {
      return
    }

    processPairEventsUpdateAction(ctx, {
      currentPairsAtom: state.value,
      update: {
        pairs: data.pairs.map((it) => pairRegistry.resolveFromDto(ctx, it)),
        updateType: data.updateType,
      },
      maxCount: 20,
    })
  }, 'handlePairEvents')

  onConnect(pairsAtom, (ctx) => {
    // The socket pushes one trending list — ours. A column ranked another way
    // would be shown for a second and then replaced by that one, so it reads
    // for itself instead. No loss: those lists are ranked over a day and do
    // not move between two polls.
    if (!live) {
      initialLoadAction(ctx)
      const timer = window.setInterval(() => {
        initialLoadAction(ctx)
      }, POLL_MS)
      return () => {
        window.clearInterval(timer)
      }
    }

    const unsubscribePairEvents = subscribeOnPairEventsAction(ctx, {
      event: PoolEvent.Trending,
      handler: handlePairEvents,
      maxCount: 20,
    })

    // Subscribing is answered with the list as it stands, so the fetch is
    // only for a socket that does not connect. Same bargain the explore
    // columns make, and the same wait.
    const fallback = window.setTimeout(() => {
      if (!AsyncStateN.isSuccess(ctx.get(pairsAtom))) {
        initialLoadAction(ctx)
      }
    }, SNAPSHOT_GRACE_MS)

    return () => {
      window.clearTimeout(fallback)
      unsubscribePairEvents()
    }
  })

  return pairsAtom
}

/**
 * One model per chain and per way of ranking.
 *
 * Kept rather than rebuilt so that switching back to a list already read
 * shows it at once, and so each model keeps its own subscription: the two
 * are different lists, not one list sorted twice.
 */
const models = new Map<string, ReturnType<typeof reatomTrendingPairs>>()

const modelFor = (network: Network.Network, strategy: string) => {
  const key = `${network}|${strategy}`
  const existing = models.get(key)
  if (existing) {
    return existing
  }
  const model = reatomTrendingPairs(network, strategy)
  models.set(key, model)
  return model
}

export const trendingPairsModelAtom = atom((ctx) => {
  const network = ctx.spy(selectedNetworkAtom)
  const strategy = ctx.spy(trendingStrategyAtom)
  return modelFor(network, strategy)
}, 'trendingPairsModelAtom')

export const trendingPairsStateAtom = atom((ctx) => {
  const model = ctx.spy(trendingPairsModelAtom)
  const state = ctx.spy(model)
  return state.pipe(
    AsyncStateN.map((it) => {
      const items = ctx.spy(it)

      return pipe(items, Array.sort(reatomPair.cmpTrending(ctx)))
    }),
  )
}, 'trendingPairsStateAtom')

export const trendingPairsAtom = atom((ctx) => {
  return ctx.spy(trendingPairsStateAtom).value ?? []
}, 'trendingPairsStateAtom')

export { fetchTrendingPairsRequestAction } from './trending-requests'
export {
  DEFAULT_TRENDING_STRATEGY,
  setTrendingStrategyAction,
  trendingIsLiveAtom,
  trendingPollAtom,
  trendingStrategiesStateAtom,
  trendingStrategyAtom,
} from './trending-strategy'
